import React, { useState } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { FileText, Trash2, Download, Image as ImageIcon } from 'lucide-react';
import { TimelineAttachment, isImageAttachment } from '../../types/timeline';
import { getAttachmentDownloadUrl } from '../../Helpers/TimelineAttachmentUtils';
import ClickableImage from '../ClickableImage';
import PhotoGallery from '../PhotoGallery';
import { PhotoModel } from '../../types/protocols';
import { orderPhotos } from '../../Helpers/photoOrdering';

interface TimelineAttachmentsProps {
    attachments: { [key: string]: TimelineAttachment };
    buildingKey: string;
    onRemove?: (attachmentKey: string) => void;
    showRemoveButton?: boolean;
    compact?: boolean;
}

const TimelineAttachments: React.FC<TimelineAttachmentsProps> = ({
    attachments,
    buildingKey,
    onRemove,
    showRemoveButton = false,
    compact = false
}) => {
    const [downloading, setDownloading] = useState<Set<string>>(new Set());

    const handleDownload = async (attachment: TimelineAttachment) => {
        const attachmentKey = attachment.key;
        if (downloading.has(attachmentKey)) return;

        setDownloading(prev => new Set(prev).add(attachmentKey));
        try {
            const url = await getAttachmentDownloadUrl(attachment.storagePath);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error downloading attachment:', error);
            alert('Kunde inte ladda ner filen');
        } finally {
            setDownloading(prev => {
                const newSet = new Set(prev);
                newSet.delete(attachmentKey);
                return newSet;
            });
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    const getFileIcon = (fileType: string) => {
        const iconSize = compact ? 16 : 20;
        switch (fileType.toLowerCase()) {
            case 'pdf':
                return <FileText size={iconSize} className="text-danger" />;
            case 'doc':
            case 'docx':
                return <FileText size={iconSize} className="text-primary" />;
            case 'xls':
            case 'xlsx':
                return <FileText size={iconSize} className="text-success" />;
            default:
                return <FileText size={iconSize} />;
        }
    };

    if (Object.keys(attachments).length === 0) {
        return null;
    }

    const imageAttachments: { [key: string]: PhotoModel } = {};
    const fileAttachments: { [key: string]: TimelineAttachment } = {};

    // Separate images from files
    Object.entries(attachments).forEach(([key, attachment]) => {
        if (isImageAttachment(attachment)) {
            imageAttachments[key] = attachment as PhotoModel;
        } else {
            fileAttachments[key] = attachment;
        }
    });

    return (
        <div className="timeline-attachments">
            {/* Image Attachments */}
            {Object.keys(imageAttachments).length > 0 && (
                <div className={`timeline-attachments-images ${compact ? 'mb-2' : 'mb-3'}`}>
                    {!compact && (
                        <div className="d-flex align-items-center mb-2">
                            <ImageIcon size={16} className="me-1" />
                            <small className="text-muted">Bilder ({Object.keys(imageAttachments).length})</small>
                        </div>
                    )}
                    {showRemoveButton && onRemove ? (
                        // Edit mode: per-image layout with remove buttons
                        <div className="d-flex flex-wrap gap-2">
                            {orderPhotos(imageAttachments).map((photo) => {
                                const key = photo.key;
                                // Check if it's a TimelineImageAttachment with fileName
                                const fileName = 'fileName' in photo && typeof photo.fileName === 'string' ? photo.fileName : undefined;
                                return (
                                    <div key={key} className="d-flex align-items-center gap-2">
                                        <div className="timeline-attachment-image-wrapper">
                                            <ClickableImage
                                                photo={photo}
                                                buildingKey={buildingKey}
                                                size={compact ? 60 : 80}
                                                altText={fileName ?? 'Timeline image'}
                                                thumbnail={true}
                                            />
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="timeline-attachment-remove-btn"
                                            onClick={() => onRemove(key)}
                                            title="Ta bort"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // Display mode: gallery with zoom and multi-photo navigation
                        <PhotoGallery
                            photos={imageAttachments}
                            buildingKey={buildingKey}
                            thumbnailSize={compact ? 60 : 80}
                        />
                    )}
                </div>
            )}

            {/* File Attachments */}
            {Object.keys(fileAttachments).length > 0 && (
                <div className="timeline-attachments-files">
                    {!compact && (
                        <div className="d-flex align-items-center mb-2">
                            <FileText size={16} className="me-1" />
                            <small className="text-muted">Filer ({Object.keys(fileAttachments).length})</small>
                        </div>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                        {Object.entries(fileAttachments).map(([key, attachment]) => {
                            if (isImageAttachment(attachment)) return null;
                            
                            const fileType = attachment.fileType || 'file';
                            const fileName = attachment.fileName || 'Okänd fil';
                            const fileSize = attachment.fileSize || 0;

                            return (
                                <div key={key} className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 timeline-attachment-file-btn"
                                        onClick={() => handleDownload(attachment)}
                                        disabled={downloading.has(key)}
                                    >
                                        {getFileIcon(fileType)}
                                        <span className="timeline-attachment-file-name">{fileName}</span>
                                        <Badge bg="secondary" className="ms-1">
                                            {formatFileSize(fileSize)}
                                        </Badge>
                                        <Download size={14} />
                                    </Button>
                                    {showRemoveButton && onRemove && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="timeline-attachment-remove-btn"
                                            onClick={() => onRemove(key)}
                                            title="Ta bort"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineAttachments;

